/*
 * Where one POV group launches (2026-10-20), shown under the group's name on
 * the Preview step: its Facebook page, campaign and ad set by name. Each
 * group has its own Settings, so seeing them side by side catches a group
 * pointed at the wrong page before anything goes to Meta.
 *
 * The lookups reuse the Settings step's cached queries, so this adds no
 * AdManage calls. An id that has not loaded yet shows as the raw id.
 */

import { Text } from "@mantine/core";
import { useLaunchAdsets, useLaunchCampaigns, useLaunchProfiles } from "../../api/queries";
import type { LaunchDefaults } from "./Step3LaunchDefaults";

export function GroupTargetSummary({
  productKey,
  values,
}: {
  productKey: string;
  values: LaunchDefaults;
}) {
  const profiles = useLaunchProfiles(productKey, true);
  const campaigns = useLaunchCampaigns(productKey, true);
  const adsets = useLaunchAdsets(productKey, values.target_campaign_id);

  const pageName =
    profiles.data?.profiles.find((p) => p.page_id === values.page_id)?.name ?? values.page_id;
  const campaignName =
    campaigns.data?.campaigns.find((c) => c.id === values.target_campaign_id)?.name ??
    values.target_campaign_id;
  const adsetName =
    adsets.data?.adsets.find((a) => a.id === values.source_adset_id)?.name ??
    values.source_adset_id;

  const parts: Array<[string, string]> = [
    ["Page", pageName],
    ["Campaign", campaignName],
    [values.into_existing_adset ? "Launching into" : "Duplicating", adsetName],
  ];

  return (
    <Text fz="sm" c="dimmed" mb="sm" data-testid="group-target-summary">
      {parts
        .filter(([, v]) => v)
        .map(([label, v], i) => (
          <span key={label}>
            {i > 0 && " · "}
            {label}: <Text span fz="sm" fw={600} c="var(--mantine-color-text)">{v}</Text>
          </span>
        ))}
    </Text>
  );
}
